"use client";
import { useEffect, useRef } from "react";

/**
 * Animated deep-space starfield — parallax drift + twinkle + occasional shooting
 * star. Ported from the design handoff (starfield.js) into a self-contained
 * React canvas. Renders a fixed full-viewport <canvas id="starfield"> behind
 * everything (z-index handled by the #starfield rule in globals.css).
 */
interface StarfieldProps {
  density?: number;
}

interface Star {
  x: number; y: number; z: number; r: number; base: number;
  tw: number; tws: number; tint: string; vx: number;
}
interface Shooting {
  x: number; y: number; len: number; max: number; ang: number; life: number;
}

const TINTS = [
  "255,255,255", "210,225,255", "255,255,255", "190,210,255",
  "255,236,210", "255,255,255", "255,210,190", "210,255,240",
];

const Starfield: React.FC<StarfieldProps> = ({ density = 1 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let W = 0, H = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let stars: Star[] = [];
    let shooting: Shooting | null = null;
    let raf = 0, last = 0;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    function build() {
      const base = Math.round((W * H) / 5200);
      const n = Math.max(60, Math.round(base * density));
      stars = [];
      for (let i = 0; i < n; i++) {
        const depth = Math.random();
        stars.push({
          x: Math.random() * W,
          y: Math.random() * H,
          z: depth,
          r: 0.35 + depth * 1.5,
          base: 0.25 + depth * 0.7,
          tw: Math.random() * Math.PI * 2,
          tws: 0.6 + Math.random() * 1.8,
          tint: TINTS[(Math.random() * TINTS.length) | 0],
          vx: -0.06 - depth * 0.16,
        });
      }
    }
    function resize() {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas!.width = W * dpr; canvas!.height = H * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
    }
    function maybeShoot() {
      if (shooting || reduced) return;
      if (Math.random() < 0.0026) {
        const sx = Math.random() * W * 0.7 + W * 0.2;
        const sy = Math.random() * H * 0.4;
        shooting = { x: sx, y: sy, len: 0, max: 140 + Math.random() * 120, ang: Math.PI * (0.18 + Math.random() * 0.12), life: 1 };
      }
    }
    function frame(t: number) {
      const dt = Math.min(50, t - last); last = t;
      ctx!.clearRect(0, 0, W, H);
      for (const s of stars) {
        s.x += s.vx * (dt / 16);
        if (s.x < -2) s.x = W + 2;
        s.tw += (s.tws * dt) / 1000;
        const a = reduced ? s.base : s.base * (0.6 + 0.4 * Math.sin(s.tw));
        ctx!.beginPath();
        ctx!.fillStyle = `rgba(${s.tint},${a.toFixed(3)})`;
        ctx!.arc(s.x, s.y, s.r, 0, 6.283);
        ctx!.fill();
        if (s.z > 0.82) {
          ctx!.fillStyle = `rgba(${s.tint},${(a * 0.18).toFixed(3)})`;
          ctx!.beginPath(); ctx!.arc(s.x, s.y, s.r * 2.6, 0, 6.283); ctx!.fill();
        }
      }
      maybeShoot();
      if (shooting) {
        const sp = (dt / 16) * 9;
        shooting.len = Math.min(shooting.max, shooting.len + sp * 6);
        shooting.x += Math.cos(shooting.ang) * sp;
        shooting.y += Math.sin(shooting.ang) * sp;
        shooting.life -= dt / 900;
        const ex = shooting.x - Math.cos(shooting.ang) * shooting.len;
        const ey = shooting.y - Math.sin(shooting.ang) * shooting.len;
        const g = ctx!.createLinearGradient(shooting.x, shooting.y, ex, ey);
        g.addColorStop(0, `rgba(190,225,255,${Math.max(0, shooting.life).toFixed(2)})`);
        g.addColorStop(1, "rgba(190,225,255,0)");
        ctx!.strokeStyle = g; ctx!.lineWidth = 1.6; ctx!.beginPath();
        ctx!.moveTo(shooting.x, shooting.y); ctx!.lineTo(ex, ey); ctx!.stroke();
        if (shooting.life <= 0 || shooting.x > W + 40 || shooting.y > H + 40) shooting = null;
      }
      raf = requestAnimationFrame(frame);
    }

    window.addEventListener("resize", resize);
    resize();
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [density]);

  return <canvas id="starfield" ref={canvasRef} aria-hidden="true" />;
};

export default Starfield;
